import { DataExportDatasetDefinition } from "../models/serviceModels/DataExport";

const RETIRED_DATASET_IDS = new Set([
    "participant_organization_links",
    "participant_races",
    "household_member_races",
    "form_versions",
    "form_fields",
    "form_field_options",
    "form_submission_fields",
    "organization_users"
]);

const PACKAGE_DESCRIPTIONS: Record<string, string> = {
    organization_setup: "Organization profile, configuration, identifiers, and users with their roles.",
    participants: "Participant profiles, assignment state, activation history, and household members with demographics resolved.",
    forms: "Form definitions with versions, fields, field types, and selectable options consolidated into one CSV.",
    form_submissions: "Form submissions flattened into columns using each form's latest published fields.",
    case_plans: "Case plans, goals, tasks, notes, and resolved goal status history.",
    reports: "Report definitions and saved report criteria.",
    report_groups: "Report groups and dashboard pin configuration."
};

const FILE_PRESENTATION: Record<string, Pick<DataExportDatasetDefinition, "description" | "details">> = {
    organization: {
        description: "Organization profile and settings with billing and plan names resolved.",
        details: ["Billing and plan reference IDs are replaced by their display names."]
    },
    participants: {
        description: "One row per participant with organization assignment and configured demographic values.",
        details: [
            "Race values are consolidated onto the participant row.",
            "Hidden demographic fields are omitted.",
            "Gender and race/ethnicity values follow the organization's configured modes."
        ]
    },
    household_members: {
        description: "One row per household member with relationship and configured demographic values.",
        details: [
            "Race values are consolidated onto the household member row.",
            "Relationship, gender, ethnicity, and race reference IDs are replaced by display values.",
            "Hidden demographic fields and configured demographic modes are respected."
        ]
    },
    participant_notes: {
        description: "Participant notes with the note source resolved to its display value."
    },
    forms: {
        description: "One row per form version field with form metadata and JSON-serialized field options.",
        details: [
            "Includes form and version metadata with field name, label, and type.",
            "Selectable options are serialized into the field_options column."
        ]
    },
    form_submissions: {
        description: "One row per form instance with submitted values flattened into field columns.",
        details: [
            "Columns start from each form's latest published version.",
            "Older submission values are matched to current fields by field name.",
            "Values for fields no longer present in the latest version are not included."
        ]
    },
    case_plan_goal_status_activity: {
        description: "Case plan goal status history with status names resolved."
    },
    report_results: {
        description: "Stored report results with status names resolved."
    },
    users: {
        description: "Organization users with roles resolved; external authentication IDs and passwords are excluded.",
        details: ["The external auth_id and all password fields are never included."]
    },
    audit_trail: {
        description: "Organization audit events with event types resolved."
    }
};

const normalizeIncludedDataset = (
    dataset: DataExportDatasetDefinition
): DataExportDatasetDefinition => {
    const presentation = FILE_PRESENTATION[dataset.id];
    return {
        ...dataset,
        description: presentation?.description || dataset.description,
        details: presentation?.details || dataset.details,
        fileName: dataset.fileName || `${dataset.id}.csv`
    };
};

const getIncludedExportFiles = (
    dataset: DataExportDatasetDefinition
): DataExportDatasetDefinition[] => {
    if (dataset.includedDatasets?.length) {
        return dataset.includedDatasets;
    }

    return [normalizeIncludedDataset(dataset)];
};

const normalizeDataExportDatasetDefinitions = (
    datasets: DataExportDatasetDefinition[]
): DataExportDatasetDefinition[] => {
    return datasets
        .filter(dataset => !RETIRED_DATASET_IDS.has(dataset.id))
        .map(dataset => {
            const includedDatasets = getIncludedExportFiles(dataset)
                .filter(included => !RETIRED_DATASET_IDS.has(included.id))
                .map(normalizeIncludedDataset);

            return {
                ...dataset,
                description: PACKAGE_DESCRIPTIONS[dataset.id] || dataset.description,
                includedDatasets
            };
        })
        .filter(dataset => dataset.includedDatasets.length > 0);
};

const getSelectedExportFileCount = (
    datasets: DataExportDatasetDefinition[]
): number => {
    return new Set(datasets.flatMap(dataset => getIncludedExportFiles(dataset).map(file => file.id))).size;
};

export {
    RETIRED_DATASET_IDS,
    getIncludedExportFiles,
    getSelectedExportFileCount,
    normalizeDataExportDatasetDefinitions
};
